import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_MAX_HP = 100;

export function useBattleOverlay() {
  const [battleState, setBattleState] = useState(null);
  const hideTimerRef = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const onBattleStart = useCallback((data) => {
    clearHideTimer();
    setBattleState({
      active: Boolean(data?.active ?? true),
      broadcasterId: data?.broadcasterId || null,
      startedAt: data?.startedAt || null,
      endedAt: null,
      team1Hp: Number.isFinite(Number(data?.team1Hp)) ? Number(data.team1Hp) : DEFAULT_MAX_HP,
      team2Hp: Number.isFinite(Number(data?.team2Hp)) ? Number(data.team2Hp) : DEFAULT_MAX_HP,
      winnerTeam: null,
      reason: null,
      lastEvent: data?.lastEvent || null,
      team1Members: Array.isArray(data?.team1Members) ? data.team1Members : [],
      team2Members: Array.isArray(data?.team2Members) ? data.team2Members : [],
      registrationDeadlineAt:
        typeof data?.registrationDeadlineAt === 'string'
          ? data.registrationDeadlineAt
          : null,
    });
  }, [clearHideTimer]);

  const onBattleState = useCallback((data) => {
    setBattleState((prev) => {
      const next = {
        ...(prev || {}),
        active: Boolean(data?.active),
        broadcasterId: data?.broadcasterId || prev?.broadcasterId || null,
        startedAt: data?.startedAt || prev?.startedAt || null,
        endedAt: data?.endedAt || prev?.endedAt || null,
        team1Hp: Number.isFinite(Number(data?.team1Hp)) ? Number(data.team1Hp) : prev?.team1Hp ?? DEFAULT_MAX_HP,
        team2Hp: Number.isFinite(Number(data?.team2Hp)) ? Number(data.team2Hp) : prev?.team2Hp ?? DEFAULT_MAX_HP,
        lastEvent: data?.lastEvent || prev?.lastEvent || null,
        team1Members: Array.isArray(data?.team1Members)
          ? data.team1Members
          : prev?.team1Members ?? [],
        team2Members: Array.isArray(data?.team2Members)
          ? data.team2Members
          : prev?.team2Members ?? [],
        registrationDeadlineAt:
          data && Object.prototype.hasOwnProperty.call(data, 'registrationDeadlineAt')
            ? data.registrationDeadlineAt
            : prev?.registrationDeadlineAt ?? null,
      };
      return next;
    });
  }, []);

  const onBattleEnd = useCallback((data) => {
    clearHideTimer();
    setBattleState((prev) => ({
      ...(prev || {}),
      active: false,
      endedAt: data?.endedAt || new Date().toISOString(),
      team1Hp: Number.isFinite(Number(data?.team1Hp)) ? Number(data.team1Hp) : prev?.team1Hp ?? DEFAULT_MAX_HP,
      team2Hp: Number.isFinite(Number(data?.team2Hp)) ? Number(data.team2Hp) : prev?.team2Hp ?? DEFAULT_MAX_HP,
      winnerTeam: data?.winnerTeam || null,
      reason: data?.reason || null,
      lastEvent: data?.lastEvent || prev?.lastEvent || null,
      team1Members: Array.isArray(data?.team1Members)
        ? data.team1Members
        : prev?.team1Members ?? [],
      team2Members: Array.isArray(data?.team2Members)
        ? data.team2Members
        : prev?.team2Members ?? [],
      registrationDeadlineAt:
        typeof data?.registrationDeadlineAt === 'string'
          ? data.registrationDeadlineAt
          : null,
    }));

    // Laisse le résultat visible quelques secondes puis cache le HUD.
    hideTimerRef.current = setTimeout(() => {
      setBattleState(null);
      hideTimerRef.current = null;
    }, 6000);
  }, [clearHideTimer]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  return {
    battleState,
    onBattleStart,
    onBattleState,
    onBattleEnd,
  };
}

